import { SubmitButton } from 'components/common/buttons/SubmitButton';
import InvoiceIssueModal from 'components/common/InvoiceIssueModal';
import PdfForm from 'components/Pdf/PdfForm';
import ExcelJS from 'exceljs';
import jsPDF from 'jspdf';
import React, { useEffect, useRef, useState } from 'react';
import toast from 'react-hot-toast';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate, useParams } from 'react-router-dom';
import Api from 'services/api';
import { fetchClientMainAccountInvoice } from 'slices/mainAccount/mainAccountInvoiceSlice';
import { mainAccountInvoiceSelector } from 'slices/mainAccount/mainAccountInvoiceSlice';
import { buildInvoiceGroups, buildSpecialProjectGroups, getSpecialProjectGroupByConfig, getInvoiceGroupByConfig } from 'helpers/invoiceGrouping';
import moment from 'moment';
import Swal from 'sweetalert2';

const AllPdfView = () => {
    const dispatch = useDispatch();
    const navigate = useNavigate();
    const { id } = useParams();
    const invoiceRef = useRef(null);
    const [buttonLoader, setButtonLoader] = useState(false);
    const [generatedInvoice, setGeneratedInvoice] = useState(null);
    const [showIssueModal, setShowIssueModal] = useState(false);
    const [submittingIssue, setSubmittingIssue] = useState(false);

    const { mainAccountInvoice, loader } = useSelector(mainAccountInvoiceSelector);
    // const generatedInvoice = Array.isArray(mainAccountInvoice) ? mainAccountInvoice?.find(t => t.id === Number(id)) : mainAccountInvoice;
    const invoiceNumber = generatedInvoice?.invoice_number;
    const clientData = generatedInvoice?.client || generatedInvoice;

    const show = (value) => value !== null && value !== undefined && value !== '' && value !== 'N/A';

    const formatCurrency = (value) => {
        const numericValue = Number(value || 0);
        return Number.isFinite(numericValue) ? numericValue.toFixed(2) : '0.00';
    };

    const formatDate = (value) => {
        if (!value) return '';
        return new Date(value).toLocaleDateString('en-GB');
    };

    const getExceededTime = (scheduledDate, scheduledEnd, actualDate, actualEnd) => {
        if (!scheduledDate || !scheduledEnd || !actualDate || !actualEnd) return null;

        const scheduledEndMoment = moment(`${scheduledDate} ${scheduledEnd}`, 'YYYY-MM-DD HH:mm:ss');
        const actualEndMoment = moment(`${actualDate} ${actualEnd}`, 'YYYY-MM-DD HH:mm:ss');

        if (!scheduledEndMoment.isValid() || !actualEndMoment.isValid()) return null;

        const exceededSeconds = actualEndMoment.diff(scheduledEndMoment, 'seconds');
        if (exceededSeconds <= 0) return null;

        return moment.utc(exceededSeconds * 1000).format('HH:mm:ss');
    };

    const getFullDuration = (actualStartDate, actualStartTime, actualEndDate, actualEndTime) => {
        if (!actualStartDate || !actualStartTime || !actualEndDate || !actualEndTime) return null;

        const startMoment = moment(`${actualStartDate} ${actualStartTime}`, 'YYYY-MM-DD HH:mm:ss');
        const endMoment = moment(`${actualEndDate} ${actualEndTime}`, 'YYYY-MM-DD HH:mm:ss');

        if (!startMoment.isValid() || !endMoment.isValid()) return null;

        const durationMinutes = endMoment.diff(startMoment, 'minutes');
        const hours = Math.floor(durationMinutes / 60);
        const minutes = durationMinutes % 60;

        if (hours > 0 && minutes > 0) return `${hours} hour ${minutes} minute`;
        if (hours > 0) return `${hours} hour`;
        if (minutes > 0) return `${minutes} minute`;
        return '0 minute';
    };

    const renderActualStartTime = (checkinDate, checkinTime, checkoutDate, checkoutTime) => {
        if (!checkinTime || !checkinDate) return null;

        const crossesMidnight = checkoutDate &&
            moment(checkoutDate).format('YYYY-MM-DD') !== moment(checkinDate).format('YYYY-MM-DD');

        if (crossesMidnight) {
            return {
                label: 'Actual Start Date Time',
                value: `${moment(checkinDate).format('DD/MM/YYYY')} ${moment(checkinTime, 'HH:mm:ss').format('h:mm A')}`
            };
        }

        return {
            label: 'Actual Start Time',
            value: moment(checkinTime, 'HH:mm:ss').format('h:mm A'),
        };
    };

   useEffect(() => {
        const loadInvoice = async () => {
            const response = await dispatch(fetchClientMainAccountInvoice(id));

            if (response) {
                setGeneratedInvoice(response.data);
            }
        };

        if (id) {
            loadInvoice();
        }
    }, [dispatch, id]);

    // const handleGeneratePdf = () => {
    //     if (!invoiceNumber) {
    //         toast.error("Invoice number not found!", {
    //             className: "errorToast",
    //         });
    //         return;
    //     }

    //     setButtonLoader(true);
    //     const doc = new jsPDF('p', 'pt', 'a4');

    //     doc.html(invoiceRef.current, {
    //         callback: function (doc) {
    //             doc.save(`${invoiceNumber}.pdf`);
    //             toast.success("Invoice downloaded successfully!", {
    //                 className: "successToast",
    //             });
    //             setButtonLoader(false);
    //         },
    //         margin: [7, 7, 7, 7],
    //         x: 7,
    //         y: 7,
    //         autoPaging: 'text',
    //         html2canvas: {
    //             scale: 0.73,
    //             useCORS: true,
    //         },
    //     });
    // };

    const handleGeneratePdf = () => {
        if (!invoiceNumber) {
            toast.error("Invoice number not found!", {
                className: "errorToast",
            });
            return;
        }

        setButtonLoader(true);

        const img = new Image();
        img.src = "/images/watermark-dark.png";

        img.onload = () => {
            const doc = new jsPDF('p', 'pt', 'a4');

            doc.html(invoiceRef.current, {
                callback: function (doc) {
                    const totalPages = doc.internal.getNumberOfPages();
                    
                    const pageWidth = doc.internal.pageSize.getWidth();
                    const pageHeight = doc.internal.pageSize.getHeight();

                    const imgWidth = 300; 
                    const imgHeight = (img.height / img.width) * imgWidth;

                    const x = (pageWidth - imgWidth) / 2;
                    const y = (pageHeight - imgHeight) / 2;

                    for (let i = 1; i <= totalPages; i++) {
                        doc.setPage(i);

                        if (doc.saveGraphicsState) {
                            doc.saveGraphicsState();
                        }

                        if (doc.GState) {
                            doc.setGState(new doc.GState({ opacity: 0.15 }));
                        }

                        doc.addImage(
                            img, 
                            'PNG',
                            x, 
                            y, 
                            imgWidth, 
                            imgHeight
                        );

                        if (doc.restoreGraphicsState) {
                            doc.restoreGraphicsState();
                        }
                    }

                    doc.save(`${invoiceNumber}.pdf`);
                    
                    toast.success("Invoice downloaded successfully!", {
                        className: "successToast",
                    });
                    setButtonLoader(false);
                },

                margin: [7, 7, 7, 7],
                x: 7,
                y: 7,
                autoPaging: "text",

                html2canvas: {
                    scale: 0.73,
                    useCORS: true,
                }
            });
        };
        img.onerror = (err) => {
            console.error("Image load hone mein masla hua:", err);
            toast.error("Watermark logo load nahi ho saka!");
            setButtonLoader(false);
        };
    };

    const handleGenerateExcel = async () => {
        if (!invoiceNumber) {
            toast.error('Invoice number not found!', {
                className: 'errorToast',
            });
            return;
        }

        setButtonLoader(true);

        try {
            const workbook = new ExcelJS.Workbook();
            workbook.creator = 'Mayabridge';
            const worksheet = workbook.addWorksheet('Invoice');
            worksheet.columns = [
                // { width: 12 },
                // { width: 40 },
                // { width: 24 },
                { width: 8 },
                { width: 26 },
                { width: 36 },
                { width: 18 },
            ];

            const logoResponse = await fetch('/main-logo.png');
            const logoData = await logoResponse.blob();
            const logoBase64 = await new Promise((resolve, reject) => {
                const reader = new FileReader();
                reader.onloadend = () => resolve(reader.result.split(',')[1]);
                reader.onerror = reject;
                reader.readAsDataURL(logoData);
            });

            const logoId = workbook.addImage({ base64: logoBase64, extension: 'png' });

            worksheet.mergeCells('A1:D3');

            worksheet.addImage(logoId, {
                tl: { col: 1, row: 0.2 },
                br: { col: 3, row: 2.8 },
            });

            worksheet.getCell('A4').value = `INVOICE # ${invoiceNumber}`;
            worksheet.getCell('D4').value = `Date: ${formatDate(generatedInvoice?.billing_date || new Date())}`;
            worksheet.getCell('A4').font = { bold: true, size: 12 };
            worksheet.getCell('D4').font = { bold: true, size: 12 };

            const addSpecialProjectSection = (startRow, isCompressed = false) => {
                const projects = clientData?.special_project || [];
                if (projects.length === 0) return startRow;
                const specialProjectGroupByValue = clientData?.invoice_group_by || '';
                const specialProjectGroupByConfig = getSpecialProjectGroupByConfig(specialProjectGroupByValue);
                const groupedProjects = buildSpecialProjectGroups(projects, specialProjectGroupByConfig.join(','));

                let currentRow = startRow + 1;
                worksheet.mergeCells(currentRow, 1, currentRow, 4);
                worksheet.getCell(currentRow, 1).value = 'SPECIAL PROJECT';
                worksheet.getCell(currentRow, 1).font = { bold: true, size: 12 };
                currentRow += 1;

                worksheet.getCell(currentRow, 1).value = '#';
                worksheet.getCell(currentRow, 2).value = 'Description';
                worksheet.getCell(currentRow, 3).value = 'Value';
                worksheet.getCell(currentRow, 4).value = 'Cost';
                [1, 2, 3, 4].forEach((column) => {
                    const cell = worksheet.getCell(currentRow, column);
                    cell.font = { color: { argb: 'FFFFFFFF' }, size: 12 };
                    cell.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FF015969' } };
                });
                currentRow += 1;

                let projectIndex = 0;
                groupedProjects.forEach((group) => {
                    if (specialProjectGroupByConfig.length > 0) {
                        worksheet.mergeCells(currentRow, 1, currentRow, 4);
                        worksheet.getCell(currentRow, 1).value = group.label;
                        worksheet.getCell(currentRow, 1).font = { bold: true, size: 12 };
                        currentRow += 1;
                    }
                    group.projects.forEach((project) => {
                    const translation = project?.translation || {};
                    const details = translation?.translation_details || {};
                    const targetLanguages = translation?.translation_target_languages
                        ?.map((target) => target?.language?.name)
                        .filter(Boolean)
                        .join(', ');
                    const projectStartRow = currentRow;
                    const addProjectField = (label, value) => {
                        if (value === null || value === undefined || value === '' || value === 'N/A') return;
                        worksheet.getCell(currentRow, 2).value = label;
                        worksheet.getCell(currentRow, 3).value = value;
                        currentRow += 1;
                    };

                    if (isCompressed) {
                        addProjectField('Project Number', translation?.transid);
                    } else {
                        addProjectField('Project Number', translation?.transid);
                        addProjectField('Date', translation?.created_at && formatDate(translation.created_at));
                        // addProjectField('Status', translation?.status);
                        addProjectField('SubAccount', translation?.subclient?.subaccount_name);
                        addProjectField('Department', translation?.translation_details?.department?.department_name);
                        if (Number(clientData?.interpreter_name_show_invoice) === 1) {
                            const vendorName = [project?.interpreter?.first_name, project?.interpreter?.last_name].filter(Boolean).join(' ');
                            addProjectField('Vendor Name', vendorName);
                        }
                        addProjectField('Source Language', translation?.language?.name);
                        addProjectField('Target Language', targetLanguages);
                        addProjectField('Notes', details?.notes || details?.comment);
                        if (Number(clientData?.requester_details_show_invoice) === 1) {
                            addProjectField('Requester Name', translation?.requester_name);
                            addProjectField('Requester Email', details?.requester_email);
                            addProjectField('Requester Phone', details?.requester_phone);
                        }
                        addProjectField('Current Phase', details?.current_phase);
                        addProjectField(
                            (() => {
                                const phase = details?.current_phase?.toLowerCase();
                                if (phase === 'review') return 'Total Page Count';
                                if (phase === 'transcription' || phase === 'other') return 'Total Audio Minutes';
                                return 'Total Word Count';
                            })(),
                            translation?.total_words_count
                        );

                        const rateUnit = (() => {
                            const phase = details?.current_phase?.toLowerCase();
                            if (phase === 'review') return 'Per Page';
                            if (phase === 'transcription' || phase === 'other') return 'Per Minute';
                            return 'Per Word';
                        })();
                        addProjectField('Rate', `$${project?.rate || 0} ${rateUnit}`);
                        if (Number(project?.rush_fee || 0) > 0) addProjectField('Rush Fee', `$${project.rush_fee}`);
                        if (Number(project?.formatting || 0) > 0) addProjectField('Formatting', `$${project.formatting}`);
                    }

                    const projectEndRow = currentRow - 1;
                    if (projectEndRow >= projectStartRow) {
                        worksheet.mergeCells(projectStartRow, 1, projectEndRow, 1);
                        worksheet.mergeCells(projectStartRow, 4, projectEndRow, 4);
                        worksheet.getCell(projectStartRow, 1).value = ++projectIndex;
                        worksheet.getCell(projectStartRow, 1).alignment = { vertical: 'top', horizontal: 'left' };
                        worksheet.getCell(projectStartRow, 4).value = `$${formatCurrency(project?.amount)}`;
                        worksheet.getCell(projectStartRow, 4).alignment = { vertical: 'top', horizontal: 'left' };
                    }

                    currentRow += 1;
                    });
                });

                return currentRow;
            };

            const isCompressedInvoice = generatedInvoice?.invoice_type === 'compressed';

            if (isCompressedInvoice) {
                let currentRow = 5;

                const addSectionRow = (label, value, rowIndex) => {
                    worksheet.getCell(rowIndex, 1).value = label;
                    worksheet.getCell(rowIndex, 2).value = value;
                };

                worksheet.getCell(currentRow, 1).value = 'Bill To';
                worksheet.getCell(currentRow, 1).font = { bold: true, size: 12 };
                currentRow++;

                if (show(clientData?.user?.name)) {
                    addSectionRow(clientData.user.name, '', currentRow++);
                }

                if (show(clientData?.billing_person)) {
                    addSectionRow(clientData.billing_person, '', currentRow++);
                }

                if (show(clientData?.billing_email)) {
                    addSectionRow(clientData.billing_email, '', currentRow++);
                }

                if (show(clientData?.billing_zipcode || clientData?.city?.name)) {
                    addSectionRow(
                        `${clientData?.billing_zipcode || ''} ${clientData?.city?.name || ''}`.trim(),
                        '',
                        currentRow++
                    );
                }

                if (show(clientData?.state?.name)) {
                    addSectionRow(clientData.state.name, '', currentRow++);
                }

                if (clientData?.appointments?.length > 0) {
                    currentRow += 1;

                worksheet.mergeCells(currentRow, 1, currentRow, 4);
                worksheet.getCell(currentRow, 1).value = 'INTERPRETATION';
                worksheet.getCell(currentRow, 1).font = { bold: true, size: 12 };
                currentRow += 1;

                worksheet.getCell(currentRow, 1).value = '#';
                worksheet.getCell(currentRow, 2).value = 'Description';
                worksheet.getCell(currentRow, 3).value = 'Appointment Number';
                worksheet.getCell(currentRow, 4).value = 'Cost';
                [1, 2, 3, 4].forEach(c => {
                    const cell = worksheet.getCell(currentRow, c);
                    cell.font = { bold: false, color: { argb: 'FFFFFFFF' }, size: 12 };
                    cell.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FF015969' } };
                });
                currentRow += 1;

                (clientData?.appointments || []).forEach((val, index) => {
                    worksheet.getCell(currentRow, 1).value = index + 1;
                    worksheet.getCell(currentRow, 2).value = 'Appointment Number';
                    worksheet.getCell(currentRow, 3).value = val?.appid || `Appointment ${index + 1}`;
                    worksheet.getCell(currentRow, 4).value = `$${formatCurrency(val?.invoice?.total_amount)}`;
                    currentRow += 1;
                });
                }

                currentRow = addSpecialProjectSection(currentRow, true);
                worksheet.mergeCells(currentRow, 1, currentRow, 3);
                worksheet.getCell(currentRow, 1).value = 'Total Due';
                worksheet.getCell(currentRow, 1).font = { bold: true };
                worksheet.getCell(currentRow, 1).alignment = { horizontal: 'right' };
                // worksheet.getCell(currentRow, 4).value = `$${formatCurrency(clientData?.appointments?.reduce((sum, item) => sum + (item?.invoice?.total_amount || 0), 0))}`;
                worksheet.getCell(currentRow, 4).value = `$${formatCurrency(
                    (clientData?.appointments || []).reduce((sum, item) => sum + (item?.invoice?.total_amount || 0), 0) +
                    (clientData?.special_project || []).reduce((sum, project) => sum + Number(project?.amount || 0), 0)
                )}`;
                worksheet.getCell(currentRow, 4).font = { bold: true };
            } else {
                const addSectionRow = (label, value, rowIndex) => {
                    worksheet.getCell(rowIndex, 1).value = label;
                    worksheet.getCell(rowIndex, 2).value = value;
                };

                let currentRow = 5;
                worksheet.getCell(currentRow, 1).value = 'Bill To';
                worksheet.getCell(currentRow, 1).font = { bold: true, size: 12 };
                currentRow += 1;

            if (show(clientData?.user?.name)) {
                addSectionRow(clientData.user.name, '', currentRow);
                currentRow += 1;
            }
            if (show(clientData?.billing_person)) {
                addSectionRow(clientData.billing_person, '', currentRow);
                currentRow += 1;
            }
            if (show(clientData?.billing_email)) {
                addSectionRow(clientData.billing_email, '', currentRow);
                currentRow += 1;
            }
            if (show(clientData?.billing_zipcode || clientData?.city?.name)) {
                addSectionRow(`${clientData?.billing_zipcode || ''} ${clientData?.city?.name || ''}`.trim(), '', currentRow);
                currentRow += 1;
            }
            if (show(clientData?.state?.name)) {
                addSectionRow(clientData.state.name, '', currentRow);
                currentRow += 1;
            }

            if (clientData?.appointments?.length > 0) {
            currentRow += 1;
            worksheet.mergeCells(currentRow, 1, currentRow, 4);
            worksheet.getCell(currentRow, 1).value = 'INTERPRETATION';
            worksheet.getCell(currentRow, 1).font = { bold: true, size: 12 };
            currentRow += 1;

            worksheet.getCell(currentRow, 1).value = '#';
            worksheet.getCell(currentRow, 2).value = 'Description';
            worksheet.getCell(currentRow, 3).value = 'Value';
            worksheet.getCell(currentRow, 4).value = 'Cost';
            [1, 2, 3, 4].forEach(c => {
                const cell = worksheet.getCell(currentRow, c);

                cell.font = {
                    bold: false,
                    color: { argb: 'FFFFFFFF' },
                    size: 12
                };

                cell.fill = {
                    type: 'pattern',
                    pattern: 'solid',
                    fgColor: { argb: 'FF015969' }
                };
            });
            currentRow += 1;

            const groupByConfig = getInvoiceGroupByConfig(clientData?.invoice_group_by || '');
            const groupedAppointments = buildInvoiceGroups(clientData?.appointments || [], groupByConfig.join(','));

            groupedAppointments.forEach((group, groupIndex) => {
                if (groupByConfig.length > 0) {
                    worksheet.mergeCells(currentRow, 1, currentRow, 4);
                    worksheet.getCell(currentRow, 1).value = group.label;
                    currentRow += 1;
                }

                group.appointments.forEach((val, index) => {
                    const totalHours = val?.invoice?.total_hours;
                    const totalMinutes = totalHours
                        ? (parseInt(totalHours.split(':')[0], 10) * 60) + parseInt(totalHours.split(':')[1], 10)
                        : 0;
                    const minDuration = val?.invoice?.min_duration;
                    const minDurUnit = val?.invoice?.duration_unit;
                    const minDurationMinutes = minDurUnit === 'hour' ? minDuration * 60 : minDuration;
                    const isValid = totalMinutes >= minDurationMinutes;
                    const inc = val?.invoice?.incremental;
                    const incremental =
                        inc === '30_minute' ? '30 minute' :
                        inc === '1_hour' ? 'hourly' :
                        inc === 'minute' ? 'per minute' : null;

                    const exceededTime = getExceededTime(
                        val.date,
                        val.end_time,
                        val.appointment_assign?.checkin_date,
                        val.appointment_assign?.checkout_time
                    );
                    const fullDuration = getFullDuration(
                        val.appointment_assign?.checkin_date,
                        val.appointment_assign?.checkin_time,
                        val.appointment_assign?.checkout_date,
                        val.appointment_assign?.checkout_time
                    );
                    // const hasCustomPricing = !!val?.appointment_custom_pricing;
                    const customPricing = val?.appointment_custom_pricing;

                    const hasCustomPricing =
                        Number(customPricing?.mileage_total || 0) > 0 ||
                        Number(customPricing?.travel_total || 0) > 0 ||
                        Number(customPricing?.discount || 0) > 0 ||
                        Number(val?.invoice?.rush_fee || 0) > 0;

                    const addSectionRow = (label, value, rowIndex) => {
                        worksheet.getCell(rowIndex, 2).value = label;
                        worksheet.getCell(rowIndex, 3).value = value;
                    };
                    const apptStartRow = currentRow;

                    if (show(val?.appid)) {
                        addSectionRow('Appointment Number', val.appid, currentRow);
                        currentRow += 1;
                    }
                    if (show(val?.language?.name)) {
                        addSectionRow('Language', val.language.name, currentRow);
                        currentRow += 1;
                    }
                    if (show(val?.status) && val.status !== 'completed') {
                        addSectionRow('Status', val.status, currentRow);
                        currentRow += 1;
                    }
                    if (show(val?.date)) {
                        const timezone = val?.appointment_details?.timezone?.name;
                        
                        let formattedDate = moment(val.date).format('DD/MM/YYYY');
    
                        if (timezone) {
                            const dateTime = moment.tz(
                                `${val.date} ${val.start_time || '00:00:00'}`,
                                'YYYY-MM-DD HH:mm:ss',
                                timezone
                            );
    
                            formattedDate = `${dateTime.format('DD/MM/YYYY')} (${dateTime.format('z')} / GMT${dateTime.format('Z')})`;
                        }
                        addSectionRow('Date', formattedDate, currentRow);
                        // addSectionRow('Date', `${moment(val.date).format('DD/MM/YYYY')} (${val?.appointment_details?.timezone?.abbreviation || ''} / GMT${val?.appointment_details?.timezone?.utc_offset || ''})`.trim(), currentRow);
                        currentRow += 1;
                    }
                    if (show(val?.type)) {
                        const minDurationText = minDuration ? ` (min duration: ${minDuration} ${minDurUnit || ''})`.trim() : '';
                        addSectionRow('Modality', `${val.type}${minDurationText ? ' ' + minDurationText : ''}`, currentRow);
                        currentRow += 1;
                    }
                    if (show(val?.subject)) {
                        addSectionRow('Type', val.subject + (incremental ? ` (Incremental: ${incremental})` : ''), currentRow);
                        currentRow += 1;
                    }
                    if (show(val?.start_time) && show(val?.date)) {
                        const scheduledCrossesMidnight = val?.end_time &&
                            moment(val.end_time, 'HH:mm:ss').isBefore(moment(val.start_time, 'HH:mm:ss'));

                        if (scheduledCrossesMidnight) {
                            addSectionRow(
                                'Scheduled Start Date Time',
                                `${moment(val.date).format('DD/MM/YYYY')} ${moment(val.start_time, 'HH:mm:ss').format('h:mm A')}`,
                                currentRow
                            );
                        } else {
                            addSectionRow('Scheduled Start Time', moment(val.start_time, 'HH:mm:ss').format('h:mm A'), currentRow);
                        }
                        currentRow += 1;
                    } else if (show(val?.start_time)) {
                        addSectionRow('Scheduled Start Time', moment(val.start_time, 'HH:mm:ss').format('h:mm A'), currentRow);
                        currentRow += 1;
                    }
                    if (show(val?.estimated_duration)) {
                        addSectionRow('Scheduled Duration', `${val?.estimated_duration} ${val?.estimated_duration_unit}`, currentRow);
                        currentRow += 1;
                    }
                    const actualStart = renderActualStartTime(
                        val?.appointment_assign?.checkin_date,
                        val?.appointment_assign?.checkin_time,
                        val?.appointment_assign?.checkout_date,
                        val?.appointment_assign?.checkout_time
                    );

                    if (actualStart) {
                        addSectionRow(actualStart.label, actualStart.value, currentRow);
                        currentRow++;
                    }
                    if (fullDuration) {
                        addSectionRow('Actual Duration', fullDuration, currentRow);
                        currentRow += 1;
                    }
                    if (fullDuration) {
                        addSectionRow('Total Billable Duration', `${val?.invoice?.extra_duration || 0} minute`, currentRow);
                        currentRow += 1;
                    }
                    if (show(val?.invoice?.rate)) {
                        addSectionRow('Rate', `$${val.invoice.rate}/${val.invoice.rate_unit || ''}`, currentRow);
                        currentRow += 1;
                    }
                    if (val?.invoice?.rush_fee > 0) {
                        addSectionRow('Rush Fee', `$${val.invoice.rush_fee}`, currentRow);
                        currentRow += 1;
                    }
                    if (show(val?.appointment_details?.facility?.facility_name)) {
                        addSectionRow('Facility Name', val.appointment_details.facility.facility_name, currentRow);
                        currentRow += 1;
                    }
                    if (show(val?.appointment_details?.requester_name)) {
                        addSectionRow('Requestor Name', val.appointment_details.requester_name, currentRow);
                        currentRow += 1;
                    }
                    if (show(val?.appointment_details?.requester_email)) {
                        addSectionRow('Requestor Email', val.appointment_details.requester_email, currentRow);
                        currentRow += 1;
                    }
                    if (show(val?.appointment_details?.requester_phone)) {
                        addSectionRow('Requestor Phone', val.appointment_details.requester_phone, currentRow);
                        currentRow += 1;
                    }
                    // if (show(val?.interpreter && `${val.interpreter.code}`)) {
                    //     addSectionRow('Interpreter Id', `${val.interpreter.code}`, currentRow);
                    if (show(val?.interpreter && `${val.interpreter.id}`)) {
                        addSectionRow('Vendor Id', `${val.interpreter.id}`, currentRow);
                        currentRow += 1;
                    }
                    if (show(val?.interpreter && `${val.interpreter.first_name} ${val.interpreter.last_name}`)) {
                        addSectionRow('Vendor Name', `${val.interpreter.first_name} ${val.interpreter.last_name}`, currentRow);
                        currentRow += 1;
                    }
                    if (show(val?.appointment_assign?.comments)) {
                        addSectionRow('Comments', val.appointment_assign.comments, currentRow);
                        currentRow += 1;
                    }

                    const dynamicFieldsList = (() => {
                        try {
                            const parsed = val?.appointment_details?.dynamic_fields
                                ? JSON.parse(val.appointment_details.dynamic_fields)
                                : [];
                            return Array.isArray(parsed) ? parsed.filter(f => f?.show_in_invoice === true) : [];
                        } catch {
                            return [];
                        }
                    })();

                    if (dynamicFieldsList.length > 0) {
                        addSectionRow('Extra Fields', '', currentRow);
                        worksheet.getCell(currentRow, 2).font = { bold: true };
                        currentRow += 1;

                        dynamicFieldsList.forEach((f) => {
                            if (f?.value) {
                            addSectionRow(f?.name || '', f?.value || '', currentRow);
                            currentRow += 1;
                            }
                        });
                    }

                    if (val?.invoice) {
                        if (hasCustomPricing) {
                            addSectionRow('Cost Breakdown', '', currentRow);
                            worksheet.getCell(currentRow, 2).font = { bold: true };
                            currentRow += 1;
                            addSectionRow('Rate', `$${val?.invoice?.rate}/${val.invoice.rate_unit || ''} × ${val?.invoice?.extra_duration || 0} minutes`, currentRow);
                            currentRow += 1;
                            if (val?.appointment_custom_pricing?.mileage_total > 0) {
                                addSectionRow('+ Mileage Total', `$${val.appointment_custom_pricing.mileage_total}`, currentRow);
                                currentRow += 1;
                            }
                            if (val?.appointment_custom_pricing?.travel_total > 0) {
                                addSectionRow('+ Travel Total', `$${val.appointment_custom_pricing.travel_total}`, currentRow);
                                currentRow += 1;
                            }
                            // if ((val?.appointment_custom_pricing?.sub_total > 0 && val?.appointment_custom_pricing?.discount > 0)) {
                            //     addSectionRow('= Sub Total', `$${val.appointment_custom_pricing.sub_total}`, currentRow);
                            //     currentRow += 1;
                            // }
                            if (val?.appointment_custom_pricing?.discount > 0) {
                                addSectionRow('+ Discount', `${val.appointment_custom_pricing.discount}%`, currentRow);
                                currentRow += 1;
                            }
                            if (val?.invoice?.rush_fee > 0) {
                                addSectionRow('+ Rush Fee', `$${val.invoice.rush_fee}`, currentRow);
                                currentRow += 1;
                            }
                            addSectionRow('= Total Cost', `$${formatCurrency(val?.invoice?.total_amount)}`, currentRow);
                            currentRow += 1;
                        } else {
                            addSectionRow('Total Cost', `$${formatCurrency(val?.invoice?.total_amount)}`, currentRow);
                            currentRow += 1;
                        }
                    }
                    const apptEndRow = currentRow - 1;
                    if (apptEndRow >= apptStartRow) {
                        if (apptEndRow > apptStartRow) {
                            worksheet.mergeCells(apptStartRow, 1, apptEndRow, 1);
                            worksheet.mergeCells(apptStartRow, 4, apptEndRow, 4);
                        }
                        worksheet.getCell(apptStartRow, 1).value = index + 1;
                        worksheet.getCell(apptStartRow, 1).alignment = { vertical: 'top', horizontal: 'left' };

                        worksheet.getCell(apptStartRow, 4).value = `$${formatCurrency(val?.invoice?.total_amount)}`;
                        worksheet.getCell(apptStartRow, 4).alignment = { vertical: 'top', horizontal: 'left' };
                    }

                    currentRow += 1;
                });
            });
        }

                currentRow = addSpecialProjectSection(currentRow, false);

                worksheet.mergeCells(currentRow, 1, currentRow, 3);
                worksheet.getCell(currentRow, 1).value = 'Total Due';
                worksheet.getCell(currentRow, 1).alignment = { horizontal: 'right' };
                // worksheet.getCell(currentRow, 4).value = `$${formatCurrency(clientData?.appointments?.reduce((sum, item) => sum + (item?.invoice?.total_amount || 0), 0))}`;
                worksheet.getCell(currentRow, 4).value = `$${formatCurrency(
                    (clientData?.appointments || []).reduce((sum, item) => sum + (item?.invoice?.total_amount || 0), 0) +
                    (clientData?.special_project || []).reduce((sum, project) => sum + Number(project?.amount || 0), 0)
                )}`;
            }

            const buffer = await workbook.xlsx.writeBuffer();
            const blob = new Blob([buffer], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });
            const url = window.URL.createObjectURL(blob);
            const anchor = document.createElement('a');
            anchor.href = url;
            anchor.download = `${invoiceNumber || 'invoice'}.xlsx`;
            anchor.click();
            window.URL.revokeObjectURL(url);

            toast.success('Excel downloaded successfully!', {
                className: 'successToast',
            });
        } catch (error) {
            console.error('Unable to generate Excel invoice', error);
            toast.error('Unable to generate Excel invoice', {
                className: 'errorToast',
            });
        } finally {
            setButtonLoader(false);
        }
    };

    const handleReportIssue = async (message) => {
        if (!generatedInvoice?.id) {
            return;
        }

        setSubmittingIssue(true);
        const response = await Api.reportInvoiceIssue({ invoice_id: generatedInvoice.id, clientId: message.clientId, client_name: message.client_name, issueDescription: message.issueDescription, invoiceNumber: message.invoiceNumber });
        setSubmittingIssue(false);

        if (response?.status >= 200 && response?.status < 300) {
            toast.success('Invoice issue reported successfully.', { className: 'successToast' });
            setShowIssueModal(false);
        } else {
            toast.error(response?.data?.message || 'Unable to report invoice issue.', { className: 'errorToast' });
        }
    };

    const handleDownloadSelection = async () => {
        const result = await Swal.fire({
            title: 'Download Invoice',
            text: 'Choose the file format you want to download.',
            showDenyButton: true,
            confirmButtonText: 'Download PDF',
            denyButtonText: 'Download Excel',
            reverseButtons: true,
            customClass: {
                denyButton: 'swal-btn',
                confirmButton: 'swal-btn',
            },
        });

        if (result.isConfirmed) {
            handleGeneratePdf();
        } else if (result.isDenied) {
            handleGenerateExcel();
        }
    };

    return (
        <div className="content">
            <div className='row_savebtn'>
                <div className='d-flex align-items-center gap-2'>
                    <button className="main_heading back_button" onClick={() => navigate(-1)}>
                        <i className="fa-solid fa-arrow-left"></i> Back
                    </button>
                </div>
                <div className='d-flex align-items-center gap-2'>
                    {generatedInvoice?.status !== 'Paid' && (
                        <SubmitButton
                            handleSubmit={() => setShowIssueModal(true)}
                            buttonType={'button'}
                            loader={false}
                            btnText={'Report Issue'}
                        />
                    )}
                    <SubmitButton
                        handleSubmit={handleDownloadSelection}
                        buttonType={'button'}
                        loader={buttonLoader}
                        btnText={'Download Invoice'}
                    />
                </div>
            </div>
            <div ref={invoiceRef} className='view_pdf_main'>
                <PdfForm data={generatedInvoice} loader={loader} type={generatedInvoice?.invoice_type}/>
            </div>
            <InvoiceIssueModal
                isOpen={showIssueModal}
                onClose={() => setShowIssueModal(false)}
                invoiceNumber={generatedInvoice?.invoice_number || invoiceNumber}
                invoiceId={generatedInvoice?.id}
                clientName={generatedInvoice?.client?.user?.name || generatedInvoice?.client?.name || ''}
                clientId={generatedInvoice?.client?.id} 
                onSubmit={handleReportIssue}
                loading={submittingIssue}
            />
        </div>
    );
};

export default AllPdfView;
